import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { compact } from "lodash";
import { deepFreeze, formatCurrency, timestampToDate } from "../util/misc";

export const SALE_MISC_ITEM_FIELDS = {
  itemType: "misc",
  line: 0,
  category: "",
  description: "",
  brand: "",
  margin: null,
  cost: null,
  price: null,
  notax: false,
  fullyInvoiced: false,
  // Carried through from the quote item. `sanitize` drops anything not
  // listed here, so without this the sale (and every invoice built from it)
  // would lose track of which line is the shipping charge.
  isShipping: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALE_MISC_ITEM_FIELDS);

export default class SaleMiscItem extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    data = sanitize(data, SALE_MISC_ITEM_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.permissions = Object.freeze({
      create: this.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  canBeCreatedBy() {
    return false;
  }

  canBeEditedBy(user) {
    const sale = this.sale;
    return user.isEmployee && sale && !sale.locked;
  }

  canBeDeletedBy() {
    return false;
  }

  // Database

  get path() {
    return SaleMiscItem.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/misc`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(saleItem) {
    return saleItem.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new SaleMiscItem(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Formatting

  get itemType() {
    return "misc";
  }

  // Mirrors SaleItem.lineNumber — see the note there and on SaleLabor. Misc
  // items share the invoice item list, so they printed as line "0" too.
  get lineNumber() {
    return this.data.line ? String(this.data.line) : null;
  }

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get cost() {
    return this.data.cost === null ? null : formatCurrency(this.data.cost);
  }

  get price() {
    return this.data.price === null ? null : formatCurrency(this.data.price);
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  summary(length = 200) {
    const description = this.data.description.replace(
      /^\*\*+[^*]+\*\*+\s*/gm,
      ""
    );
    return length === null
      ? description
      : description.slice(0, length) + (description.length > length ? "…" : "");
  }

  toString() {
    return compact([this.data.brand, this.summary()]).join("\n");
  }
}
